import * as React from 'react';
import { cn } from '@/lib/utils';
import { HomeIcons, type IconName } from '@/assets/icons';

type AppIconProps = {
  /** Lookup by registry key, e.g. "checklist" | "slack" */
  name?: IconName;
  /** Or pass an imported icon URL directly */
  src?: string;
  /**
   * Sets both width and height in pixels.
   * Example: size={18} → 18x18, size={20} → 20x20
   */
  size?: number;
  alt?: string;
  className?: string;
} & Omit<React.ImgHTMLAttributes<HTMLImageElement>, 'src' | 'alt' | 'width' | 'height'>;

/**
 * Renders a custom icon from the central homepage icon registry.
 *
 *   <AppIcon name="checklist" size={18} alt="Checklist" />
 *   <AppIcon name="mapsAndLocation" size={20} />
 */
export function AppIcon({
  name,
  src,
  size,
  alt = '',
  className,
  style,
  ...props
}: AppIconProps) {
  const iconSrc = src ?? (name && HomeIcons ? HomeIcons[name] : undefined);

  if (!iconSrc) {
    if (process.env.NODE_ENV !== 'production') {
      console.warn('[AppIcon] Missing icon. Pass `name` or `src`.');
    }
    return null;
  }

  const sizeStyle =
    size != null
      ? { width: size, height: size, ...style }
      : style;

  return (
    <img
      src={iconSrc}
      alt={alt}
      width={size}
      height={size}
      className={cn('inline-block object-contain shrink-0', className)}
      style={sizeStyle}
      draggable={false}
      {...props}
    />
  );
}
